import { useNavigate } from 'react-router-dom';
import type { AlertRow, AlertsData } from '../../api/types.js';
import { DataTable, type Column } from '../DataTable/DataTable.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { Cell, formatRunDate, regionName } from '../Audit/shared.js';
import { accentFor, color, font, fontSize, radius } from '../../theme/tokens.js';
import { ManagerCell } from './ManagerCell.js';
import { ViolationChip } from './ViolationChip.js';
import { ageLabel, daysBetween, flaggedDate } from './model.js';

/**
 * The alerts table (blueprint lines 1174–1199): Recruiter · Leader · Manager · Flagged · Rule
 * violations, every row in the flagged tint, a row opening the recruiter's Delivery page (FR-AL17).
 * Every header carries its full label on hover and sorts the table (FR-AL10, FR-AL18).
 */

function notifiedLine(row: AlertRow): string | null {
  const n = row.notified;
  if (!n) return null;
  const daily = n.lastDaily ? formatRunDate(n.lastDaily) : null;
  const weekly = n.lastWeekly ? formatRunDate(n.lastWeekly) : null;
  if (n.mode === 'dry-run') return daily ? `Dry run: would have emailed ${daily}.` : 'Dry run: not yet recorded.';
  if (n.mode === 'redirect') return daily ? `Test email ${daily}, sent to testers.` : 'Test mode: not yet emailed.';
  if (!daily && !weekly) return 'Not yet emailed.';
  return [daily ? `Daily alert sent ${daily}` : null, weekly ? `in ${row.managerName ?? 'their manager'}’s weekly report ${weekly}` : null]
    .filter(Boolean)
    .join(' · ') + '.';
}

function FlaggedCell({ row, audit, today, floor }: { row: AlertRow; audit: string; today: string; floor: string | null }) {
  const date = flaggedDate(row, audit);
  if (!date) return <span>—</span>;
  const days = daysBetween(date.since, today);
  const face = ageLabel(days, date.lowerBound);
  const since = `Flagged since ${formatRunDate(date.since)}${date.lowerBound ? ' or earlier' : ''}.`;
  const why = date.lowerBound
    ? `It may be older: runs before ${floor ? formatRunDate(floor) : 'the history floor'} are not checked yet.`
    : null;
  const emailed = notifiedLine(row);
  return (
    <span
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
      style={{ display: 'inline-flex' }}
    >
      <Disclosure
        accessibleName={[since, why, emailed].filter(Boolean).join(' ')}
        panel={
          <span>
            <span style={{ display: 'block' }}>{since}</span>
            {why ? <span style={{ display: 'block', marginTop: 3 }}>{why}</span> : null}
            {emailed ? <span style={{ display: 'block', marginTop: 3 }}>{emailed}</span> : null}
          </span>
        }
      >
        <span className="mono" style={{ fontFamily: font.mono, fontSize: fontSize.caption, color: color.inkFaint }}>
          {face}
        </span>
      </Disclosure>
    </span>
  );
}

export function AlertsTable({
  data,
  rows,
  audit,
  today,
}: {
  data: AlertsData;
  rows: AlertRow[];
  /** The Audit-type filter — it decides each row's Flagged date (FR-AL22). */
  audit: string;
  today: string;
}) {
  const navigate = useNavigate();
  const auditById = new Map(data.audits.map((a) => [a.id, a]));
  const floor = data.history.oldestRunChecked;

  const columns: Array<Column<AlertRow>> = [
    {
      key: 'recruiter',
      label: 'Recruiter',
      fullLabel: 'Recruiter — region, name and Nursing/Allied type',
      width: '1.4fr',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.name ?? '',
      render: (r) => (
        <Cell>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
              }}
              style={{ display: 'inline-flex' }}
            >
              <Disclosure accessibleName={`${r.region}, ${regionName(r.region)}`} panel={<span>{regionName(r.region)}</span>}>
                <span
                  className="mono"
                  style={{
                    fontFamily: font.mono,
                    fontSize: '10px',
                    fontWeight: 600,
                    color: color.onAccent,
                    background: accentFor(r.region),
                    padding: '2px 6px',
                    borderRadius: radius.sm,
                  }}
                >
                  {r.region}
                </span>
              </Disclosure>
            </span>
            <span>
              <span style={{ display: 'block', fontSize: fontSize.control, fontWeight: 600, color: color.ink }}>
                {r.name ?? `Recruiter ${r.recruiterId}`}
              </span>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '10px',
                  fontWeight: 600,
                  marginTop: 2,
                  padding: '1px 7px',
                  borderRadius: radius.sm,
                  background: r.offering === 'nursing' ? color.washMint : color.washIndigo,
                  color: r.offering === 'nursing' ? color.nursing : color.allied,
                }}
              >
                {r.offering === 'nursing' ? 'Nursing' : 'Allied'}
              </span>
            </span>
          </span>
        </Cell>
      ),
    },
    {
      key: 'leader',
      label: 'Leader',
      fullLabel: 'Reporting leader — both, where the manager reports to two',
      width: '.8fr',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.leaders.join(', '),
      render: (r) => (
        <Cell>
          <span style={{ fontSize: fontSize.control, color: color.inkBody }}>{r.leaders.join(', ') || '—'}</span>
        </Cell>
      ),
    },
    {
      key: 'manager',
      label: 'Manager',
      fullLabel: 'Assigned manager — the warning sign means they cannot be sent the weekly report',
      width: '.8fr',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.managerName ?? '',
      render: (r) => (
        <Cell>
          <ManagerCell name={r.managerName} status={r.managerEmailStatus} />
        </Cell>
      ),
    },
    {
      key: 'flagged',
      label: 'Flagged',
      fullLabel: 'How long ago the current unbroken flag started',
      width: '.7fr',
      align: 'left',
      sortable: true,
      sortValue: (r) => flaggedDate(r, audit)?.since ?? '',
      render: (r) => (
        <Cell>
          <FlaggedCell row={r} audit={audit} today={today} floor={floor} />
        </Cell>
      ),
    },
    {
      key: 'violations',
      label: 'Rule violations',
      fullLabel: 'BoB rules being violated — one chip per open flag',
      width: '2.3fr',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.flags.length,
      render: (r) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '11px 4px' }}>
          {r.flags.map((f) => (
            <ViolationChip key={f.auditId} flag={f} audit={auditById.get(f.auditId)} />
          ))}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.recruiterId)}
      caption="Recruiters breaking BoB rules"
      emptyMessage="No alerts match this filter."
      onRowClick={(r) => navigate(`/delivery/recruiter/${r.recruiterId}`)}
      rowTint={() => color.flaggedRow}
    />
  );
}
